import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { 
  getNewsletterSubscribers, 
  deleteNewsletterSubscriber, 
  updateNewsletterSubscriber
} from '../../services/newsletterService';
import Pagination from '../../components/Pagination/Pagination';
import './AdminSubscribersPage.css';

const AdminSubscribersPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [countryFilter, setCountryFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 7,
    total: 0,
    totalPages: 0
  });

  // Fetch subscribers
  const fetchSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      if (countryFilter) {
        params.country = countryFilter;
      }
      
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const data = await getNewsletterSubscribers(params);
      setSubscribers(data.subscribers || []);
      setPagination(prev => ({
        ...prev,
        ...data.pagination
      }));
    } catch (err) {
      setError('Failed to fetch subscribers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, countryFilter, searchQuery]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedSubscribers([]);
    } else {
      setSelectedSubscribers(subscribers.map(s => s.id));
    }
    setSelectAll(!selectAll);
  };

  // Handle individual select
  const handleSelectSubscriber = (id) => {
    if (selectedSubscribers.includes(id)) {
      setSelectedSubscribers(selectedSubscribers.filter(s => s !== id));
    } else {
      setSelectedSubscribers([...selectedSubscribers, id]);
    }
  };

  // Handle send email
  const handleSendEmail = (email) => {
    window.location.href = `mailto:${email}`;
  };

  // Generate page numbers
  const getPageNumbers = () => {
    const pages = [];
    const maxPages = Math.min(5, pagination.totalPages);
    let startPage = Math.max(1, pagination.page - 2);
    let endPage = Math.min(pagination.totalPages, startPage + maxPages - 1);
    
    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <AdminLayout>
      <div className="admin-subscribers-page">
        {/* Page Header */}
        <div className="subscribers-page-header">
          <h1 className="subscribers-page-title">Subscriber</h1>
        </div>

        {/* Subscribers List Section */}
        <div className="subscribers-list-section">
          {/* List Header with Title, Search and Filters */}
          <div className="subscribers-list-header">
            <h2 className="subscribers-list-title">Subscriber Lists</h2>
            
            {/* Search Box */}
            <div className="subscribers-search-box">
              <input 
                type="text" 
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.166 14.168L17.4993 17.5013" stroke="#969696" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667Z" stroke="#969696" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            {/* Country Filter */}
            <div className="subscribers-country-filter">
              <select 
                value={countryFilter}
                onChange={(e) => {
                  setCountryFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <option value="">Country</option>
                <option value="UAE">UAE</option>
                <option value="Dubai">Dubai</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="Qatar">Qatar</option>
                <option value="Kuwait">Kuwait</option>
                <option value="Bahrain">Bahrain</option>
                <option value="Oman">Oman</option>
              </select>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>

          {error && <div className="subscribers-error-message">{error}</div>}

          {loading ? (
            <div className="subscribers-loading">Loading subscribers...</div>
          ) : subscribers.length === 0 ? (
            <div className="subscribers-empty-state">
              <p>No subscribers found.</p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="subscribers-table">
                {/* Table Header */}
                <div className="subscribers-table-header">
                  <div className="subscribers-th-checkbox">
                    <input 
                      type="checkbox" 
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                    <span>All</span>
                  </div>
                  <div className="subscribers-th">Name</div>
                  <div className="subscribers-th">Location</div>
                  <div className="subscribers-th">Gmail</div>
                  <div className="subscribers-th">Action</div>
                </div>

                {/* Table Body */}
                <div className="subscribers-table-body">
                  {subscribers.map((subscriber) => (
                    <div key={subscriber.id} className="subscribers-table-row">
                      <div className="subscribers-td-checkbox">
                        <input 
                          type="checkbox"
                          checked={selectedSubscribers.includes(subscriber.id)}
                          onChange={() => handleSelectSubscriber(subscriber.id)}
                        />
                      </div>
                      <div className="subscribers-td subscribers-td-name">
                        {subscriber.name || 'Eiffage Construction'}
                      </div>
                      <div className="subscribers-td">
                        {subscriber.location || subscriber.country || 'Dubai'}
                      </div>
                      <div className="subscribers-td">
                        {subscriber.email || 'company@gmail.com'}
                      </div>
                      <div className="subscribers-td-action">
                        <span 
                          className="send-email-link"
                          onClick={() => handleSendEmail(subscriber.email)}
                        >
                          Send Email
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3.33203 8H12.6654" stroke="#6D6D6D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M8 3.33203L12.6667 7.9987L8 12.6654" stroke="#6D6D6D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pagination */}
              <Pagination currentPage={pagination.page} totalPages={pagination.totalPages} onPageChange={(p) => setPagination(prev => ({ ...prev, page: p }))} />
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSubscribersPage;
